import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./PublicConsentForm.css";

interface ConsentFormData {
  candidateFirstName: string;
  candidateEmail: string;
  consentType: string;
  consentGiven: boolean;
  consentGivenAt: string | null;
  consentWithdrawn: boolean;
  formTitle: string;
  formIntro: string;
  processingText: string;
  rightsText: string;
  retentionText: string;
}

const PublicConsentForm: React.FC = () => {
  const { t } = useTranslation("public");
  const { token } = useParams<{ token: string }>();
  const [formData, setFormData] = useState<ConsentFormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasReadTerms, setHasReadTerms] = useState(false);

  useEffect(() => {
    const fetchConsentData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`/api/recruiting/public/consent/${token}`);

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          setFormData(data.data);
          // If already given, mark as submitted
          if (data.data.consentGiven) {
            setIsSubmitted(true);
          }
        } else {
          setError(data.error || data.message || t("consentForm.invalidLink"));
        }
      } catch (err) {
        setError(t("consentForm.loadError"));
        console.error("Error fetching consent data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchConsentData();
    }
  }, [token, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!hasReadTerms) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`/api/recruiting/public/consent/${token}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setIsSubmitted(true);
      } else {
        setError(data.error || data.message || t("consentForm.recordError"));
      }
    } catch (err) {
      console.error("Error submitting consent:", err);
      setError(t("consentForm.submitError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="consent-form-container">
        <div className="consent-loading">
          <div className="spinner"></div>
          <p>{t("consentForm.loading")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="consent-form-container">
        <div className="consent-error">
          <div className="error-icon">⚠️</div>
          <h2>{t("consentForm.unableToLoad")}</h2>
          <p>{error}</p>
          <p className="error-help">{t("consentForm.errorHelp")}</p>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="consent-form-container">
        <div className="consent-success">
          <div className="success-icon">✓</div>
          <h2>{t("consentForm.thankYou")}</h2>
          <p>{t("consentForm.consentRecorded")}</p>
          {formData?.consentGivenAt && (
            <p className="consent-date">
              {t("consentForm.consentRecordedOn")}{" "}
              {new Date(formData.consentGivenAt).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          )}
          <div className="success-message">
            <p>{t("consentForm.closePageMessage")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="consent-form-container">
      <div className="consent-form-wrapper">
        <div className="consent-header">
          <div className="company-logo">
            <img src="/hirefocuz-logo.png" alt="HireFocuZ" />
          </div>
          <h1>{formData?.formTitle || t("consentForm.defaultTitle")}</h1>
          {formData?.candidateFirstName && (
            <p className="greeting">
              {t("consentForm.greeting", {
                name: formData.candidateFirstName,
              })}
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="consent-form">
          <div className="consent-section">
            <h2>{t("consentForm.dataProcessingConsent")}</h2>
            <p className="intro-text">{formData?.formIntro}</p>
          </div>

          <div className="consent-section">
            <h3>{t("consentForm.howDataUsed")}</h3>
            <p>{formData?.processingText}</p>
          </div>

          <div className="consent-section">
            <h3>{t("consentForm.yourRights")}</h3>
            <p>{formData?.rightsText}</p>
          </div>

          <div className="consent-section">
            <h3>{t("consentForm.dataRetention")}</h3>
            <p>{formData?.retentionText}</p>
          </div>

          <div className="consent-checkbox-section">
            <label className="consent-checkbox">
              <input
                type="checkbox"
                checked={hasReadTerms}
                onChange={(e) => setHasReadTerms(e.target.checked)}
                disabled={isSubmitting}
              />
              <span className="checkmark"></span>
              <span className="checkbox-text">
                {t("consentForm.checkboxText")}
              </span>
            </label>
          </div>

          <div className="consent-submit-section">
            <button
              type="submit"
              className="btn-consent-submit"
              disabled={!hasReadTerms || isSubmitting}
            >
              {isSubmitting
                ? t("consentForm.submitting")
                : t("consentForm.giveConsent")}
            </button>
          </div>

          <div className="consent-footer">
            <p className="footer-note">{t("consentForm.footerNote")}</p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PublicConsentForm;
